import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";
import collaboratorsAPI from "./collaboratorsAPI";
import professionalProfileAPI from "./professionalProfileAPI";

// Use the configured API base URL

class ProfileDashboardService {
  constructor() {
    this.cache = new Map();
    this.apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
    });

    // Flag to track if unified API is available
    this.unifiedApiAvailable = null;

    // Add auth token to all requests
    this.apiClient.interceptors.request.use((config) => {
      // Try multiple token sources for compatibility
      let token = localStorage.getItem("authToken");

      if (!token) {
        try {
          const user = JSON.parse(localStorage.getItem("user"));
          token = user?.token;
        } catch (e) {
          // Ignore parsing errors
        }
      }

      if (!token) {
        token = localStorage.getItem("token");
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    });
  }

  /**
   * Check if unified API is available
   */
  async checkUnifiedApiAvailability() {
    if (this.unifiedApiAvailable !== null) {
      return this.unifiedApiAvailable;
    }

    try {
const response = await this.apiClient.get("/profile/dashboard/stats");
      this.unifiedApiAvailable = true;
return true;
    } catch (error) {
this.unifiedApiAvailable = false;
      return false;
    }
  }

  /**
   * Legacy API fallback - aggregates multiple API calls
   */
  async getLegacyProfileData(userId = null, currentUser) {
const startTime = Date.now();

    try {
      const isOwnProfile = !userId || userId === currentUser?.id;
      const targetUserId = userId || currentUser?.id;

      let profileData,
        statsData,
        relationshipData = null;

      if (isOwnProfile) {
        // Load own profile
[profileData] = await Promise.all([
          collaboratorsAPI.profile.getMyProfile(),
        ]);
      } else {
        // Load other user's profile
const userData = await collaboratorsAPI.profile.getUserProfile(userId);
        profileData = userData.profile;
        relationshipData = userData.relationship;
      }

      // Load additional data in parallel
const [
        stats,
        posts,
        rooms,
        communities,
        workshops,
        activityData,
        skillsData,
        ratingsData,
        ratingStats,
      ] = await Promise.all([
        professionalProfileAPI.stats
          .getProfileStats(targetUserId)
          .catch(() => ({})),
        professionalProfileAPI.posts
          .getUserPosts(targetUserId, 1, 5)
          .catch(() => []),
        professionalProfileAPI.rooms.getUserRooms(targetUserId).catch(() => []),
        professionalProfileAPI.communities
          .getUserCommunities(targetUserId)
          .catch(() => []),
        professionalProfileAPI.workshops
          .getUserWorkshops(targetUserId)
          .catch(() => []),
        professionalProfileAPI.activity
          .getUserActivity(targetUserId)
          .catch(() => []),
        collaboratorsAPI.endorsement
          .getUserSkills(targetUserId)
          .catch(() => []),
        collaboratorsAPI.rating
          .getUserRatings(targetUserId, 1, 20)
          .catch(() => ({ ratings: [] })),
        collaboratorsAPI.rating.getRatingStats(targetUserId).catch(() => ({})),
      ]);

      // Debug: Log the raw data to understand structure
// Transform to unified format
      const transformedData = {
        success: true,
        data: {
          profile: {
            ...profileData,
            averageRating: profileData?.averageRating || 0,
            totalRatings: profileData?.totalRatings || 0,
          },
          stats: {
            followersCount: stats?.followersCount || 0,
            followingCount: stats?.followingCount || 0,
            collaborationsCount: stats?.collaborationsCount || 0,
            postsCount: stats?.postsCount || 0,
            communitiesCreatedCount: stats?.communitiesCount || 0,
            researchRoomsCreatedCount: stats?.researchRoomsCount || 0,
            workshopsCreatedCount: stats?.workshopsCount || 0,
            communitiesMemberCount: 0,
            researchRoomsMemberCount: 0,
            workshopsParticipantCount: 0,
            profileCompletion: 85,
          },
          relationship: relationshipData,
          activity: {
            posts: {
              posts: Array.isArray(posts)
                ? posts
                : posts?.data?.posts || posts?.posts || [],
            },
            researchRooms: {
              rooms: Array.isArray(rooms)
                ? rooms
                : rooms?.data?.rooms || rooms?.rooms || [],
            },
            communities: {
              communities: Array.isArray(communities)
                ? communities
                : communities?.data?.communities ||
                  communities?.communities ||
                  [],
            },
            workshops: {
              workshops: Array.isArray(workshops)
                ? workshops
                : workshops?.data?.workshops || workshops?.workshops || [],
            },
            activities: {
              activities: Array.isArray(activityData)
                ? activityData
                : activityData?.activities ||
                  activityData?.data?.activities ||
                  [],
            },
            endorsements: {
              skills: Array.isArray(skillsData)
                ? skillsData
                : skillsData?.skills || skillsData?.data?.skills || [],
            },
            ratings: {
              received: {
                ratings: ratingsData?.ratings || [],
              },
              stats: ratingStats || {},
            },
            collaborations: { requests: [], type: "summary" },
            social: {
              followers: [], // Load on-demand when modal opens
              following: [], // Load on-demand when modal opens
            },
          },
          meta: {
            isOwnProfile,
            lastUpdated: new Date().toISOString(),
            fetchTime: Date.now() - startTime,
          },
        },
        cached: false,
        responseTime: Date.now() - startTime,
        legacy: true,
      };
return transformedData;
    } catch (error) {
throw error;
    }
  }

  /**
   * Get profile dashboard with smart caching and fallback
   * @param {string|null} userId - User ID (null for own profile)
   * @param {object} options - Options { forceRefresh, maxAge }
   * @returns {Promise<object>} Dashboard data
   */
  async getProfileDashboard(userId = null, options = {}) {
    const cacheKey = `dashboard_${userId || "own"}`;
    const startTime = Date.now();

    try {
      // Check local cache first
      if (!options.forceRefresh && this.cache.has(cacheKey)) {
        const cached = this.cache.get(cacheKey);
        const age = Date.now() - cached.timestamp;

        if (age < (options.maxAge || 30000)) {
return {
            ...cached.data,
            fromLocalCache: true,
            localCacheAge: Math.floor(age / 1000),
          };
        }
      }

      // Check if unified API is available
      const unifiedAvailable = await this.checkUnifiedApiAvailability();

      let result;

      if (unifiedAvailable) {
        // Try unified API
        try {
          const endpoint = userId
            ? `/profile/dashboard/${userId}`
            : "/profile/dashboard";
const response = await this.apiClient.get(endpoint);

          if (response.data.success) {
            result = response.data;
} else {
            throw new Error(response.data.message || "Unified API failed");
          }
        } catch (unifiedError) {
this.unifiedApiAvailable = false; // Mark as unavailable for this session

          // Get current user for legacy fallback
          const currentUser = JSON.parse(localStorage.getItem("user"));
          result = await this.getLegacyProfileData(userId, currentUser);
        }
      } else {
        // Use legacy API directly
        const currentUser = JSON.parse(localStorage.getItem("user"));
        result = await this.getLegacyProfileData(userId, currentUser);
      }

      // Cache the response locally
      this.cache.set(cacheKey, {
        data: result,
        timestamp: Date.now(),
      });

      const totalTime = Date.now() - startTime;
return result;
    } catch (error) {
// Return stale cache if available
      if (this.cache.has(cacheKey)) {
        const staleData = this.cache.get(cacheKey);
return {
          ...staleData.data,
          fromLocalCache: true,
          stale: true,
          error: error.message,
        };
      }

      throw error;
    }
  }

  /**
   * Invalidate local cache
   * @param {string|null} userId - User ID (null for own profile)
   */
  invalidateCache(userId = null) {
    const cacheKey = `dashboard_${userId || "own"}`;
    this.cache.delete(cacheKey);
}

  /**
   * Invalidate backend cache (only works with unified API)
   * @param {string|null} userId - User ID (null for own profile)
   */
  async invalidateBackendCache(userId = null) {
    try {
      if (this.unifiedApiAvailable === false) {
return;
      }

      const payload = userId ? { userId } : {};
      await this.apiClient.post("/profile/dashboard/invalidate", payload);
} catch (error) { /* ignored */ }
  }

  /**
   * Get cache performance statistics (only works with unified API)
   * @returns {Promise<object>} Cache stats
   */
  async getCacheStats() {
    try {
      if (this.unifiedApiAvailable === false) {
        return {
          data: {
            performance: { hitRate: "N/A (Legacy Mode)", totalRequests: 0 },
            redis: { connected: false, status: "not available" },
            memory: { keys: 0 },
          },
        };
      }

      const response = await this.apiClient.get("/profile/dashboard/stats");
      return response.data;
    } catch (error) {
return null;
    }
  }

  /**
   * Prefetch profile data (for hover preloading)
   * @param {string} userId - User ID to prefetch
   */
  async prefetchProfile(userId) {
    try {
      await this.getProfileDashboard(userId);
} catch (error) { /* ignored */ }
  }
}

export const profileDashboardService = new ProfileDashboardService();
